import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
  Alert,
} from "reactstrap";

const PTEScoreCalculator = () => {
  const [scores, setScores] = useState({
    listening: "",
    reading: "",
    writing: "",
    speaking: "",
  });

  const handleChange = (field, value) => {
    if (value === "") {
      setScores({ ...scores, [field]: "" });
      return;
    }
    const num = parseInt(value);
    if (num >= 0 && num <= 90) {
      setScores({ ...scores, [field]: value });
    }
  };

  const averageScore = useMemo(() => {
    const vals = Object.values(scores)
      .filter((v) => v !== "")
      .map(Number);
    if (vals.length === 0) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }, [scores]);

  const getIELTSBand = (pteScore) => {
    if (!pteScore) return null;
    const s = Math.round(parseFloat(pteScore));
    if (s >= 86) return 9.0;
    if (s >= 83) return 8.5;
    if (s >= 79) return 8.0;
    if (s >= 73) return 7.5;
    if (s >= 65) return 7.0;
    if (s >= 59) return 6.5;
    if (s >= 51) return 6.0;
    if (s >= 43) return 5.5;
    if (s >= 35) return 5.0;
    return 4.5;
  };

  const equivalentBand = getIELTSBand(averageScore);

  // Updated band table based on your image
  const bandTable = [
    { band: "86–90", level: "Expert" },
    { band: "83–85", level: "Very Good" },
    { band: "79–82", level: "Very Good" },
    { band: "73–78", level: "Good" },
    { band: "65–72", level: "Good" },
    { band: "59–64", level: "Competent" },
    { band: "51–58", level: "Competent" },
    { band: "43–50", level: "Modest" },
    { band: "35–42", level: "Modest" },
  ];

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem 0" }}>
      <Container style={{ maxWidth: "900px" }}>
        {/* Header */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody
            className="text-center py-4"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <h1 className="text-white fw-bold mb-2">
              🎓 PTE to IELTS Converter
            </h1>
            <p className="text-white mb-0" style={{ opacity: 0.9 }}>
              Calculate your equivalent IELTS band based on PTE Academic scores
            </p>
          </CardBody>
        </Card>

        {/* Input Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              📝 Enter Your PTE Scores
            </Label>

            <Row className="g-3">
              {Object.keys(scores).map((skill) => (
                <Col md={6} key={skill}>
                  <FormGroup>
                    <Label className="fw-semibold text-capitalize">{skill}</Label>
                    <Input
                      type="number"
                      placeholder="0 – 90"
                      value={scores[skill]}
                      onChange={(e) => handleChange(skill, e.target.value)}
                      className="py-2"
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>

            <div className="mt-3 p-3 bg-light rounded border-start border-4 border-primary">
              <small className="text-muted">
                The overall PTE score is averaged from the four sections. The equivalent IELTS band is calculated from this score.
              </small>
            </div>
          </CardBody>
        </Card>

        {/* Result Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              Your Result
            </Label>

            <Alert color="primary" className="border-0 mb-0" style={{ background: "#f0f8ff" }}>
              <Row className="align-items-center">
                <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
                  <div className="text-uppercase fw-semibold mb-1 small">
                    Overall PTE Score
                  </div>
                  <div style={{ fontSize: "2.5rem", fontWeight: "700" }}>
                    {averageScore ?? "--"}
                  </div>
                  <Badge color="primary" className="mt-2">
                    Equivalent IELTS Band
                  </Badge>
                </Col>

                <Col md={6} className="justify-content-end">
                  <Row className="g-3">
                    <Col lg={6}>
                      <div className="text-center bg-white rounded p-3">
                        <div className="text-muted small">Sections</div>
                        <div className="fw-bold fs-5">
                          {Object.values(scores).filter((v) => v !== "").length}/4
                        </div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-center bg-white rounded p-3">
                        <div className="text-muted small">Equivalent IELTS Band</div>
                        <div className="fw-bold fs-5">
                          {equivalentBand ?? "--"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Alert>
          </CardBody>
        </Card>

        {/* Reference Table */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              📋 IELTS Band Descriptors
            </Label>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>PTE Score</th>
                    <th className="text-end">Proficiency Level</th>
                  </tr>
                </thead>
                <tbody>
                  {bandTable.map((row, i) => (
                    <tr key={i}>
                      <td className="fw-bold">{row.band}</td>
                      <td className="text-end text-muted">{row.level}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-muted small mb-0">
            <strong>PTE to IELTS Converter</strong> • Official Mapping
          </p>
        </div>
      </Container>
    </div>
  );
};

export default PTEScoreCalculator;
